/**
 * Analytics service — GA4 e-commerce event tracking via GTM dataLayer.
 *
 * All events flow through `window.dataLayer` to the GTM container, which owns
 * the GA4 tag firing. Never call gtag() directly for e-commerce events.
 *
 * Rules enforced here:
 *  - `ecommerce: null` is pushed before every e-commerce event to prevent
 *    stale item arrays bleeding into subsequent events (GA4 spec).
 *  - All monetary values are JS numbers, never strings.
 *  - No `any` types.
 */

import { jwtDecode } from 'jwt-decode';
import type { JwtPayload } from '@/lib/types/jwt';
import type { ProductDetails, ProductOverview } from '@/lib/types/product';
import type { ShoppingCartItem } from '@/lib/types/cart';
import type { OrderItem } from '@/lib/types/order';
import type { InboundParams } from '@/lib/tracking/inbound-params';
import { getInboundParams } from '@/lib/tracking/inbound-params';
import type {
  Ga4Item,
  Ga4EcommerceEvent,
  SearchEventPayload,
  ViewItemListEvent,
  SelectItemEvent,
  ViewItemEvent,
  AddToCartEvent,
  ViewCartEvent,
  RemoveFromCartEvent,
  BeginCheckoutEvent,
  AddShippingInfoEvent,
  AddPaymentInfoEvent,
  PurchaseEvent,
} from './types';

// ─── Core push ───────────────────────────────────────────────────────────────

/** Safely push an entry to the GTM dataLayer. Guards `window.dataLayer`
 *  existence so a blocked GTM snippet (CSP / ad-blocker) does not throw a
 *  TypeError that would be silently swallowed by React Query mutation
 *  callbacks or React effect cleanup. Exported so non-ecommerce push sites
 *  (e.g. RouteAnalyticsTracker's page_view) can reuse the same guard. */
export function safeDataLayerPush(entry: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(entry);
}

/** Cached inbound UTM / campaign params from sessionStorage. The result never
 *  changes within a session, so we memoize at module scope to avoid a
 *  synchronous sessionStorage read on every ecommerce event push. */
let cachedInboundParams: Record<string, unknown> | null = null;

/** Build a flat object of inbound UTM / campaign params from sessionStorage so
 *  that every ecommerce event carries campaign attribution through the funnel.
 *  Returns an empty object when no params are stored (no-op merge). */
function inboundParamsPayload(): Record<string, unknown> {
  if (cachedInboundParams !== null) return cachedInboundParams;
  const p: InboundParams | null = getInboundParams();
  cachedInboundParams = p
    ? {
        utm_source: p.utm_source ?? undefined,
        utm_medium: p.utm_medium ?? undefined,
        utm_campaign: p.utm_campaign ?? undefined,
        utm_term: p.utm_term ?? undefined,
        utm_content: p.utm_content ?? undefined,
        campaign_id: p.campaign_id ?? undefined,
        original_url: p.original_url,
      }
    : {};
  return cachedInboundParams;
}

/** Push a GA4 e-commerce event to the GTM dataLayer, clearing the previous
 *  ecommerce payload first (required by GA4 to avoid stale item arrays).
 *  Merges stored inbound UTM/campaign params so attribution survives the
 *  entire funnel. Guards `window.dataLayer` existence so a blocked GTM
 *  snippet (CSP / ad-blocker) does not throw a TypeError that would be
 *  silently swallowed by React Query mutation callbacks. */
export function pushEcommerceEvent(event: Ga4EcommerceEvent): void {
  safeDataLayerPush({ ecommerce: null });
  safeDataLayerPush({ ...event, ...inboundParamsPayload() });
}

// ─── Identity ────────────────────────────────────────────────────────────────

/** Decode the nopCommerce JWT, SHA-256 hash the `CustomerId` claim, and push
 *  `user_id` to the dataLayer.  GA4 uses this for cross-device user stitching.
 *  Clears `user_id` on logout (token === null). */
export async function setUserId(token: string | null): Promise<void> {
  if (!token) {
    safeDataLayerPush({ user_id: undefined });
    return;
  }
  try {
    const decoded = jwtDecode<JwtPayload>(token);
    const customerId = decoded.CustomerId;
    if (!customerId) return;
    const data = new TextEncoder().encode(customerId);
    const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);
    const hashHex = Array.from(new Uint8Array(hashBuffer))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
    safeDataLayerPush({ user_id: hashHex });
  } catch {
    // Never block analytics flow for auth errors.
  }
}

// ─── Item mappers ────────────────────────────────────────────────────────────

/** Map a catalog `ProductOverview` (list page) to a GA4 item.
 *  Does not include brand/category — those require the full detail model. */
export function toGa4ItemFromProductOverview(
  product: ProductOverview,
  overrides?: Partial<Ga4Item>,
): Ga4Item {
  return {
    item_id: product.sku ?? String(product.id),
    item_name: product.name ?? '',
    price: product.productPrice?.priceValue ?? 0,
    quantity: 1,
    ...overrides,
  };
}

/** Map a `ProductDetails` (detail page) to a GA4 item.
 *  Includes brand from the first manufacturer and up to 3 category levels. */
export function toGa4ItemFromProductDetails(
  product: ProductDetails,
  overrides?: Partial<Ga4Item>,
): Ga4Item {
  const manufacturers = product.productManufacturers;
  const breadcrumb = product.breadcrumb?.categoryBreadcrumb ?? [];
  return {
    item_id: product.sku ?? String(product.id),
    item_name: product.name ?? '',
    item_brand: manufacturers?.[0]?.name ?? undefined,
    item_category: breadcrumb[0]?.name ?? undefined,
    item_category2: breadcrumb[1]?.name ?? undefined,
    item_category3: breadcrumb[2]?.name ?? undefined,
    price: product.productPrice?.priceValue ?? 0,
    quantity: 1,
    ...overrides,
  };
}

/** Map a `ShoppingCartItem` to a GA4 item. */
export function toGa4ItemFromCartItem(item: ShoppingCartItem): Ga4Item {
  return {
    item_id: item.sku ?? String(item.productId),
    item_name: item.productName ?? '',
    price: item.unitPriceValue,
    quantity: item.quantity,
    discount: item.discountValue > 0 ? item.discountValue : undefined,
  };
}

/** Map an `OrderItem` (from the confirmation page) to a GA4 item. */
export function toGa4ItemFromOrderItem(item: OrderItem): Ga4Item {
  return {
    item_id: item.Sku ?? String(item.ProductId),
    item_name: item.ProductName ?? '',
    price: item.UnitPriceInclTaxValue,
    quantity: item.Quantity,
  };
}

// ─── Typed track functions ───────────────────────────────────────────────────

export function trackViewItemList(
  payload: ViewItemListEvent['ecommerce'],
): void {
  pushEcommerceEvent({ event: 'view_item_list', ecommerce: payload });
}

export function trackSelectItem(
  payload: SelectItemEvent['ecommerce'],
): void {
  pushEcommerceEvent({ event: 'select_item', ecommerce: payload });
}

export function trackViewItem(payload: ViewItemEvent['ecommerce']): void {
  pushEcommerceEvent({ event: 'view_item', ecommerce: payload });
}

export function trackSearch(term: string): void {
  const searchEvent: SearchEventPayload = { event: 'search', search_term: term };
  safeDataLayerPush(searchEvent as unknown as Record<string, unknown>);
}

export function trackAddToCart(payload: AddToCartEvent['ecommerce']): void {
  pushEcommerceEvent({ event: 'add_to_cart', ecommerce: payload });
}

export function trackViewCart(payload: ViewCartEvent['ecommerce']): void {
  pushEcommerceEvent({ event: 'view_cart', ecommerce: payload });
}

export function trackRemoveFromCart(
  payload: RemoveFromCartEvent['ecommerce'],
): void {
  pushEcommerceEvent({ event: 'remove_from_cart', ecommerce: payload });
}

export function trackBeginCheckout(
  payload: BeginCheckoutEvent['ecommerce'],
  checkoutStepName: string,
): void {
  pushEcommerceEvent({
    event: 'begin_checkout',
    ecommerce: payload,
    checkout_step_name: checkoutStepName,
  });
}

export function trackAddShippingInfo(
  payload: AddShippingInfoEvent['ecommerce'],
  checkoutStepName: string,
): void {
  pushEcommerceEvent({
    event: 'add_shipping_info',
    ecommerce: payload,
    checkout_step_name: checkoutStepName,
  });
}

export function trackAddPaymentInfo(
  payload: AddPaymentInfoEvent['ecommerce'],
  checkoutStepName: string,
): void {
  pushEcommerceEvent({
    event: 'add_payment_info',
    ecommerce: payload,
    checkout_step_name: checkoutStepName,
  });
}

export function trackPurchase(payload: PurchaseEvent['ecommerce']): void {
  pushEcommerceEvent({ event: 'purchase', ecommerce: payload });
}

// ─── Attribution (once per session) ─────────────────────────────────────────

/** Push the `attribution_captured` event with stored inbound params. This is
 *  a custom (non-GA4-ecommerce) event, so it is pushed directly without the
 *  `{ ecommerce: null }` clear. */
export function trackAttributionCaptured(params: InboundParams): void {
  safeDataLayerPush({
    event: 'attribution_captured',
    utm_source: params.utm_source ?? undefined,
    utm_medium: params.utm_medium ?? undefined,
    utm_campaign: params.utm_campaign ?? undefined,
    utm_term: params.utm_term ?? undefined,
    utm_content: params.utm_content ?? undefined,
    campaign_id: params.campaign_id ?? undefined,
    original_url: params.original_url,
    captured_at: params.captured_at,
  });
}
